import React, { useState } from "react";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography
} from "@mui/material";
import Item from "../../components/Item";
import IngestService from "./IngestService";
import ConfirmDialog from "../../components/ConfirmDialog";

const AgentGuardian = ({ setting, onRefresh }) => {
    const [ingesting, setIngesting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Calculate dates for next week based on ingest_date setting
    const getNextWeekDates = (ingestDateValue) => {
        // If no ingest date is provided, use today as fallback
        const baseDate = ingestDateValue ? new Date(ingestDateValue) : new Date();
        
        const fromDate = new Date(baseDate);
        fromDate.setDate(baseDate.getDate() + 7); // ingest_date + 7 days
        
        const toDate = new Date(fromDate);
        toDate.setDate(fromDate.getDate() + 1); // fromDate + 1 day

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            fromDate: formatDate(fromDate),
            toDate: formatDate(toDate)
        };
    };

    const handleIngestNextWeek = () => {
        setShowConfirmDialog(true);
    };

    const confirmGuardianIngest = async () => {
        try {
            setIngesting(true);
            setError(null);
            setSuccess(null);

            const ingestDateValue = setting?.value;
            const { fromDate, toDate } = getNextWeekDates(ingestDateValue);
            
            const result = await IngestService.ingestArticles(fromDate, toDate);
            
            if (result.success) {
                // Handle response with ingested count
                const ingestedCount = result.data?.ingested || 0;
                setSuccess(`${ingestedCount} draft puzzle${ingestedCount !== 1 ? 's' : ''} have been created`);
                
                // Refresh all settings after ingest
                if (onRefresh) {
                    await onRefresh();
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            Logger.error("Error ingesting articles:", err);
            setError(err.message || "Failed to ingest articles");
        } finally {
            setIngesting(false);
        }
    };

    const { fromDate, toDate } = getNextWeekDates(setting?.value);

    return (
        <Item sx={{ height: '100%', width: '100%' }}>
            {setting ? (
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                            Guardian News
                        </Typography>
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Last Ingested Date:
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1 }}>
                                    {setting.value || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Next Period:
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1 }}>
                                    {fromDate} to {toDate}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleIngestNextWeek}
                                    disabled={true}
                                    sx={{ minWidth: 140 }}
                                >
                                    {ingesting ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Progressing...
                                        </>
                                    ) : (
                                        "Execute"
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Alert severity="info">
                            Scheduled: 9:00 AM Monday and Friday
                        </Alert>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                            Guardian News
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No setting found
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <ConfirmDialog
                open={showConfirmDialog}
                setOpen={setShowConfirmDialog}
                title="Confirm Guardian Articles Ingest"
                message={`Are you sure you want to ingest Guardian articles for the period ${fromDate} to ${toDate}?`}
                callback={confirmGuardianIngest}
            />
        </Item>
    );
};

export default AgentGuardian;
